/**
 * Card selector for choosing a place type during onboarding.
 * Single-select design - user picks one type like "Apartment", "Warehouse", "Office", etc.
 *
 * Loads place types from the backend industry presets.
 * Styled to match SpaceCardSelector and AssetCardSelector for visual consistency.
 */

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBed,
  faBuilding,
  faBuildingUser,
  faCheck,
  faCogs,
  faDoorOpen,
  faHardHat,
  faHome,
  faHotel,
  faHouse,
  faHouseChimneyWindow,
  faIndustry,
  faLocationDot,
  faStore,
  faToolbox,
  faTree,
  faTruck,
  faUmbrellaBeach,
  faWarehouse,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useIndustryPresetQuery } from '~/generated/graphql';

// =============================================================================
// ICON MAPPING FOR PLACE TYPES
// =============================================================================

/**
 * Maps place type names to their icons.
 * Used to display icons for backend-provided place types.
 */
const PLACE_TYPE_ICONS: Record<string, IconDefinition> = {
  // Holiday Rentals & Property Management
  'Short-term rental': faHotel,
  'Long-term rental': faHouse,
  Apartment: faBuilding,
  House: faHouse,
  Villa: faUmbrellaBeach,
  Cabin: faHouseChimneyWindow,
  Unit: faDoorOpen,
  Townhouse: faHome,
  Studio: faBed,
  Hotel: faHotel,
  'Storage unit': faWarehouse,

  // Cleaning & Laundry Services
  Office: faBuildingUser,
  'Retail store': faStore,
  Warehouse: faWarehouse,
  Workshop: faToolbox,
  'Laundry facility': faWarehouse,

  // Facilities Maintenance
  Gym: faBuilding,
  'Community venue': faBuilding,
  'School / Childcare': faBuilding,

  // Trades & Construction
  'Residential home': faHouse,
  'Job site': faHardHat,
  Depot: faTruck,
  'Strata lot': faBuilding,
  'Client site': faLocationDot,

  // Industrial
  Factory: faIndustry,
  Plant: faCogs,

  // Fleet & Transport
  'Rental branch': faStore,

  // Other / Generic
  Shop: faStore,
  Yard: faTree,
  Other: faBuilding,
};

/**
 * Get icon for a place type name, with fallback to generic building icon.
 */
const getPlaceTypeIcon = (name: string): IconDefinition => {
  return PLACE_TYPE_ICONS[name] ?? faBuilding;
};

// =============================================================================
// TYPES
// =============================================================================

type PlaceTypeSuggestion = {
  name: string;
  icon: IconDefinition;
};

// =============================================================================
// PLACE TYPE CARD COMPONENT
// =============================================================================

type PlaceTypeCardProps = {
  placeType: PlaceTypeSuggestion;
  selected: boolean;
  onSelect: () => void;
};

function PlaceTypeCard({ placeType, selected, onSelect }: PlaceTypeCardProps) {
  return (
    <button
      type='button'
      onClick={onSelect}
      className={classNames(
        'group relative flex flex-col items-center justify-center rounded-xl border-2 p-3 transition-all duration-200',
        'min-h-[100px] w-full',
        selected
          ? 'bg-brand/5 border-brand shadow-md'
          : 'border-grey-20 bg-white hover:border-grey-40 hover:shadow-sm'
      )}
      aria-label={`Select ${placeType.name}`}
      aria-pressed={selected}
      tabIndex={0}
    >
      {/* Check indicator */}
      {selected && (
        <div className='absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white'>
          <FontAwesomeIcon icon={faCheck} className='h-3 w-3' />
        </div>
      )}

      {/* Icon */}
      <div
        className={classNames(
          'mb-2 flex h-8 w-8 items-center justify-center rounded-full transition-colors',
          selected ? 'text-brand' : 'text-grey-50 group-hover:text-grey-70'
        )}
      >
        <FontAwesomeIcon icon={placeType.icon} className='h-5 w-5' />
      </div>

      {/* Label */}
      <span
        className={classNames(
          'text-center text-sm font-medium',
          selected ? 'text-brand' : 'text-grey-70'
        )}
      >
        {placeType.name}
      </span>
    </button>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export type PlaceTypeCardSelectorProps = {
  industry: string | null;
  selectedType: string | null;
  onTypeSelect: (type: string) => void;
};

export function PlaceTypeCardSelector({
  industry,
  selectedType,
  onTypeSelect,
}: PlaceTypeCardSelectorProps) {
  // Fetch industry-specific place types from backend
  const [{ data, fetching }] = useIndustryPresetQuery({
    variables: { industry: industry || '' },
    pause: !industry, // Don't fetch until industry is selected
  });

  // Map backend strings to PlaceTypeSuggestion objects with icons
  // Sort to show "Office" first when available, then "Other" at the end
  const placeTypes = useMemo((): PlaceTypeSuggestion[] => {
    const backendTypes = data?.industryPreset?.siteTypes ?? [];
    const mapped = backendTypes.map((name) => ({
      name,
      icon: getPlaceTypeIcon(name),
    }));

    // Add "Other" option if not already present
    const hasOther = mapped.some((type) => type.name === 'Other');
    if (!hasOther) {
      mapped.push({
        name: 'Other',
        icon: getPlaceTypeIcon('Other'),
      });
    }

    // Move "Office" to front if present, keep "Other" at the end
    return mapped.sort((a, b) => {
      if (a.name === 'Office') return -1;
      if (b.name === 'Office') return 1;
      if (a.name === 'Other') return 1;
      if (b.name === 'Other') return -1;
      return 0;
    });
  }, [data?.industryPreset?.siteTypes]);

  // Show loading state while fetching
  if (fetching && placeTypes.length === 0) {
    return (
      <div className='flex h-[200px] items-center justify-center'>
        <div className='text-grey-50'>Loading place types...</div>
      </div>
    );
  }

  // Show message if no industry selected
  if (!industry) {
    return (
      <div className='flex h-[200px] items-center justify-center'>
        <div className='text-grey-50'>Please select an industry first</div>
      </div>
    );
  }

  return (
    <div className='w-full'>
      <div className='grid grid-cols-3 gap-3 sm:grid-cols-4'>
        {placeTypes.map((placeType) => (
          <PlaceTypeCard
            key={placeType.name}
            placeType={placeType}
            selected={selectedType === placeType.name}
            onSelect={() => onTypeSelect(placeType.name)}
          />
        ))}
      </div>
    </div>
  );
}
